import {useCallback, useEffect} from 'react';

import {assert} from 'Asserts';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import {getRange} from 'notebook/mediocre/selection/text/SelUtil';
import isFocusSupercededByModal from 'ui-library/modals/isFocusSupercededByModal';
import * as keyboard from 'util/Keyboard';

import {MediocreSearchResult} from './SearchResults';

export const MEDIOCRE_SEARCH_RESULT_ID_DATA_ATTRIBUTE = 'data-mediocre-search-id';
const MEDIOCRE_OVERLAY_DATA_ATTRIBUTE = 'data-mediocre-overlay';
export const MEDIOCRE_OVERLAY_PROPS = {[MEDIOCRE_OVERLAY_DATA_ATTRIBUTE]: '1'};
export const MEDIOCRE_OVERLAY_SELECTOR = `[${MEDIOCRE_OVERLAY_DATA_ATTRIBUTE}=1]`;

export function getSelectedSearchResultDataAttrs(searchResultId: string | undefined): {
  [MEDIOCRE_SEARCH_RESULT_ID_DATA_ATTRIBUTE]?: string;
} {
  return typeof searchResultId === 'string'
    ? {
        [MEDIOCRE_SEARCH_RESULT_ID_DATA_ATTRIBUTE]: searchResultId,
      }
    : {};
}

export function jumpToResult(searchResult: MediocreSearchResult): void {
  // If a UUID is actually given, we don't have to worry about escaping, since those characters are safe.
  assert(
    !/[^a-fA-F0-9-]/.test(searchResult.uuid),
    'Unable to jump to search result: unexpected MediocreSearchResult.uuid value'
  );
  const element = document.querySelector(
    `[${MEDIOCRE_SEARCH_RESULT_ID_DATA_ATTRIBUTE}="${searchResult.uuid}"]`
  );
  if (!element) {
    return;
  }
  const scrollIntoView = (): void => {
    element.scrollIntoView({
      behavior: 'auto',
      inline: 'center',
      // See: https://stackoverflow.com/questions/11039885/scrollintoview-causing-the-whole-page-to-move
      block: 'nearest',
    });
  };
  scrollIntoView();
  setTimeout(scrollIntoView, 120);
}

export function useCtrlFKeydown(
  onCtrlF: (query: string | undefined) => void,
  onCtrlG: (reverse: boolean) => void,
  onEscape: () => void
): void {
  const keydownCallback = useCallback(
    (e: KeyboardEvent) => {
      if (keyboard.isEscapeKey(e)) {
        // Guarantee that the Ctrl-F closes, but wait for anyone else to handle escape first.
        setTimeout(() => {
          onEscape();
        });
      } else if (keyboard.isCtrlF(e) || keyboard.isCtrlG(e) || keyboard.isCtrlShiftG(e)) {
        // TODO(BNCH-40432): Handle Ctrl-F interaction with sequence / protein tab.

        // There are import issues related to SidepanelStore that necessitate using require here
        const SidepanelStore = require('nav/sidepanel/SidepanelStore').default;

        // If a modal is open, we disable custom Ctrl-F/Ctrl-G.
        // Native Ctrl-F/G will still work in this case.
        if (
          isFocusSupercededByModal() ||
          SidepanelStore.getIsExpanded() ||
          FullScreenStore.getCurrentScreen() != null
        ) {
          return;
        }

        e.preventDefault(); // Prevent browser's native Ctrl-F handling
        e.stopPropagation(); // Avoid other handlers from handling.

        if (keyboard.isCtrlF(e)) {
          // TODO(BNCH-40436): If a table cell is selected, we should see the Ctrl-F query with its text.
          const selectedText = getRange()?.toString();
          onCtrlF(selectedText);
        } else {
          onCtrlG(e.shiftKey);
        }
      }
    },
    [onCtrlF, onCtrlG, onEscape]
  );

  useEffect(() => {
    window.addEventListener('keydown', keydownCallback, true);
    return () => {
      window.removeEventListener('keydown', keydownCallback, true);
    };
  }, [keydownCallback]);
}
